import React, { useCallback, useRef, useState } from 'react';
import { Box, IconButton, IconPause16, IconPlay16, ResponsiveSection, useBelowBreakpoint } from '@stitch-fix/mode-react';
import { CloudinaryPicture } from '../CSCloudinaryAsset/Picture';
import { type CloudinaryImageFields } from '../CloudinaryImageWrapper';
import { CloudinaryVideo, CloudinaryVideoHandle } from '../CloudinaryVideo/CloudinaryVideo';
import { ModuleWrapper, ModuleWrapperProps } from '../ModuleWrapper';
import { ModularComponentProps } from '../ModularPageWrapper/ModularPageWrapper';
import styles from './styles.module.scss';
type DisplayMediaTextColor = 'blue-45' | 'mint-60' | 'white' | 'forest-60' | 'grapefruit-60' | 'citrus-60';
type DisplayMediaBackgroundColor = 'blue-45' | 'mint-60' | 'white' | 'gray-95' | 'gray-99';
export type DisplayMediaProps = {
  backgroundColor: DisplayMediaBackgroundColor;
  majorTitleTextColor?: DisplayMediaTextColor;
  media: {
    mobile: CloudinaryImageFields;
    desktop: CloudinaryImageFields;
  };
  minorTitleTextColor?: DisplayMediaTextColor;
} & Omit<ModuleWrapperProps, 'children'>;
export const DisplayMedia = ({
  alignment,
  backgroundColor,
  majorTitleTextColor,
  media,
  minorTitleTextColor,
  moduleTitle,
  verticalSpacing
}: DisplayMediaProps & ModularComponentProps) => {
  const isMobile = useBelowBreakpoint(560);
  const videoRef = useRef<CloudinaryVideoHandle>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const handleTogglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      } else {
        videoRef.current.play();
        setIsPlaying(true);
      }
    }
  };
  const handleVideoCanPlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {
        // Ignore play errors (e.g., user interaction required on some browsers)
      });
    }
  }, []);
  const renderVideo = (url: string, label: string) => <>
      <Box className={styles.playPauseButtonContainer}>
        <IconButton aria-label={isPlaying ? 'Pause video' : 'Plays video'} onClick={handleTogglePlayPause}>
          {isPlaying ? <IconPause16 color="white" title="Pause video" /> : <IconPlay16 color="white" title="Play video" />}
        </IconButton>
      </Box>
      <CloudinaryVideo onCanPlay={handleVideoCanPlay} ref={videoRef} videoLabel={label} videoUrl={url} />
    </>;
  const renderMedia = () => {
    if (isMobile && media.mobile.src.includes('mp4')) {
      return renderVideo(media.mobile.src, media.mobile.alt);
    }
    if (!isMobile && media.desktop.src.includes('mp4')) {
      return renderVideo(media.desktop.src, media.desktop.alt);
    }
    return <CloudinaryPicture alt={media.desktop.alt} sources={[{
      minWidth: 0,
      cloudinaryAsset: media.mobile.cloudinaryAsset
    }, {
      minWidth: 560,
      cloudinaryAsset: media.desktop.cloudinaryAsset
    }]} imageProps={{
      fetchPriority: 'low'
    }} />;
  };
  return <Box bgColor={backgroundColor} mt={verticalSpacing}>
      <ResponsiveSection gutter="none" width="xxlarge">
        <ModuleWrapper alignment={alignment} majorTitleTextColor={majorTitleTextColor} minorTitleTextColor={minorTitleTextColor} moduleTitle={moduleTitle} gutter="standard">
          <Box className={styles.container}>{renderMedia()}</Box>
        </ModuleWrapper>
      </ResponsiveSection>
    </Box>;
};